import classNames from 'classnames';
import { UIEvent, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RiArrowDownSFill, RiArrowRightSFill } from 'react-icons/ri';

import { useGetAgreementsQuery } from '@client/api';
import {
  IconButton,
  Pagination,
  Table,
  TableBody,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  Text,
} from '@client/toolkit';

import { AgreementListTableRow } from './agreement-list-row.component';

type AgreementUnassignedItemProps = {
  unassignedCount: number;
  isFetching?: boolean;
  search?: string;
};

export function AgreementUnassignedItem({ unassignedCount, isFetching, search }: AgreementUnassignedItemProps) {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');
  const { t: tType } = useTranslation('type');

  const [open, setOpen] = useState(false);
  const hasAgreements = useMemo(() => unassignedCount > 0, [unassignedCount]);

  const pageSize = 20;
  const [page, setPage] = useState(0);
  const pageOffset = page * pageSize;

  const { data, isFetching: isFetchingAgreements } = useGetAgreementsQuery(
    {
      'hasNoBuilding.Equal': true,
      'pagination.Limit': pageSize,
      'pagination.Offset': pageOffset,
      search: search || undefined,
    },
    { skip: !hasAgreements || !open },
  );

  const agreements = useMemo(() => data?.items ?? [], [data?.items]);
  const totalCount = data?.totalCount ?? 0;

  const toggleOpen = useCallback(() => setOpen((currentOpen) => !currentOpen), []);
  const onArrowClick = useCallback(
    (event: UIEvent) => {
      toggleOpen();
      event.stopPropagation();
    },
    [toggleOpen],
  );

  if ((!hasAgreements && !isFetching) || search) {
    return null;
  }

  return (
    <div className="border-t border-border">
      <div
        className={classNames(
          'relative flex flex-col items-stretch px-2 py-2',
          'cursor-pointer hover:bg-surface-hover transition ease-in-out',
        )}
        onClick={hasAgreements ? toggleOpen : undefined}
      >
        <div className="flex items-center justify-start">
          <IconButton
            color="primary"
            size="sm"
            fontSize="md"
            className={classNames('mr-2', !hasAgreements && 'invisible opacity-0')}
            onClick={onArrowClick}
          >
            {open ? <RiArrowDownSFill /> : <RiArrowRightSFill />}
            <span className="sr-only">Show Agreements</span>
          </IconButton>

          <div className="relative overflow-hidden min-w-[300px]">
            <div className="flex gap-4 items-center">
              <Text
                as="div"
                className="text-sm leading-tight text-secondary font-bold whitespace-nowrap italic"
                loading={isFetching}
              >
                {tCommon('unassigned')}
              </Text>
            </div>
            <div className="flex items-center overflow-hidden">
              <Text
                as="div"
                className="text-sm font-normal text-secondary flex-1 whitespace-nowrap"
                loading={isFetching}
              >
                {tCommon('noEntityAssigned', { entity: tGlobal('entity.building').toLowerCase() })}
              </Text>
            </div>
          </div>

          <div className="flex-1" />

          <div className="flex items-center gap-4">
            <div className="px-3 py-2 text-left min-w-[120px]">
              <Text as="div" className="text-xs text-secondary whitespace-nowrap">
                -
              </Text>
            </div>
            <div className="px-3 py-2 text-left min-w-[110px]">
              <Text as="div" className="text-xs text-secondary whitespace-nowrap">
                -
              </Text>
            </div>
            <div className="px-3 py-2 text-left min-w-[120px]">
              <Text as="div" className="text-xs text-secondary whitespace-nowrap">
                -
              </Text>
            </div>
            <div className="px-3 py-2 text-left min-w-[120px]">
              <Text as="div" className="text-xs text-secondary whitespace-nowrap">
                -
              </Text>
            </div>
          </div>
        </div>
      </div>

      {open && (
        <>
          <Table className="w-full">
            <TableHead className="bg-stone-700">
              <TableHeadRow>
                <TableHeadCell>{tType('RelationshipType.Tenant')}</TableHeadCell>
                <TableHeadCell>{tType('RelationshipType.Owner')}</TableHeadCell>
                <TableHeadCell>{tGlobal('agreement.name')}</TableHeadCell>
                <TableHeadCell>{tGlobal('agreement.durationStart')}</TableHeadCell>
                <TableHeadCell>{tGlobal('agreement.durationEnd')}</TableHeadCell>
                <TableHeadCell>{tGlobal('agreement.totalYearlyAmount')}</TableHeadCell>
                <TableHeadCell>{tGlobal('agreement.status')}</TableHeadCell>
              </TableHeadRow>
            </TableHead>
            <TableBody>
              {isFetchingAgreements &&
                Array.from(Array(2).keys()).map((idx) => <AgreementListTableRow key={idx} isFetching />)}
              {!isFetchingAgreements &&
                agreements.map((agreement) => (
                  <AgreementListTableRow key={agreement.id} agreement={agreement} isFetching={false} />
                ))}
            </TableBody>
          </Table>
          <Pagination
            className="border-t"
            totalCount={totalCount}
            currentPage={page}
            setPage={setPage}
            pageSize={pageSize}
          />
        </>
      )}
    </div>
  );
}
